<template>
  <div>
    <div v-if="!loadStatus">
      <Loading text="Loading..." />
    </div>
    <div class="logo-band" v-if="loadStatus">
      <div class="logo-container">
        <div class="gemnote-logo">
          <img
            v-if="projectInfo.teamLogo && projectInfo.teamLogo.url"
            :src="projectInfo.teamLogo.url"
            alt="logo"
          />
          <img v-else src="../../assets/gemnote_logo_pebble.png" alt="logo" />
        </div>
      </div>
    </div>
    <div class="row">
      <template v-if="loadStatus && Object.keys(projectInfo).length">
        <div
          class="left-column"
          :style="{
            backgroundImage: `url(${teamBackground})`
          }"
        ></div>
        <div class="right-column">
          <div class="main-input-form-container">
            <h2>
              <span
                class="custom-title inter"
                v-if="projectInfo.enableCustomTitle"
              >
                {{ projectInfo.adminOrderTitle }}
              </span>

              <span class="custom-title editorial" v-else>
                Welcome to <em>{{ projectTitle }}</em> <br />
                for {{ projectInfo.teamName }} !</span
              >
            </h2>
            <div v-if="projectInfo.enableCustomDescription">
              <p class="custom-description">
                {{ projectInfo.adminOrderDescription }}
              </p>
            </div>

            <div v-else class="message-holder">
              <p class="inter main-white">
                You've been invited by {{ projectInfo.name }} to submit your
                information to receive a special gift or swag.
              </p>
            </div>

            <div class="disclaimer">
              <p v-if="!verificationEmailSent">
                Please enter your email to sign in.
              </p>

              <div v-else>
                <p class="sent">
                  We have sent an email to {{ email }}. <br />
                  Please check your inbox and follow instructions to access this
                  page.
                </p>

                <!-- <p class="resend" @click="validateGroupOrderRecipient(true)">
                  Didn’t receive it? <span>Resend email</span>
                </p> -->
              </div>
            </div>

            <form
              v-if="!verificationEmailSent"
              @submit.prevent="validateGroupOrderRecipient(false)"
            >
              <div class="form-group">
                <input
                  :class="{ 'input-error': showError }"
                  data-testid="email-input"
                  type="email"
                  placeholder="Enter email"
                  class="form-control"
                  v-model="email"
                  @blur="validateDomain"
                  @focus="showError = false"
                />
                <span class="error-text" v-if="showError">Invalid email.</span>
              </div>
              <div class="form-group">
                <!-- <input
                  v-if="groupOrderDetails['passwordProtected?']"
                  placeholder="Enter access code"
                  class="form-control"
                  type="text"
                  v-model.trim="password"
                  autocomplete="off"
                /> -->
              </div>

              <div class="login-button">
                <button
                  class="whitebutton-gemnote whitebutton-gemnote--small"
                  :class="{ 'button-gemnote--disabled': disableButton }"
                  :disabled="disableButton"
                >
                  <span>Enter</span>
                </button>
              </div>
            </form>

            <div
              class="option-region hidden"
              v-if="false"
              :class="{ hidden: hideSSO }"
            >
              <p><span>or </span></p>

              <!-- Google button here -->
              <button
                class="google-btn"
                :disabled="loggingIn"
                @click="handleGoogleLogin"
              >
                <img src="../../assets/google.svg" alt="google" />
                <span>Sign in with Google</span>
              </button>
              <!-- Okta button here: HIDDEN => projectInfo.teamName -->
              <section>
                <OktaSignIn @onOktaSignIn="handleOktaSignIn" />
              </section>
            </div>
          </div>

          <div class="footer-links">
            <FooterLinks />
          </div>
        </div>
      </template>

      <!-- Not Found template here -->
      <template v-if="loadStatus && !Object.keys(projectInfo).length">
        <div
          class="left-column"
          :style="{ backgroundImage: 'url(' + gemnoteBackground + ')' }"
        ></div>
        <div class="right-column disabled">
          <div class="main-input-form-container">
            <h2>Project page not found!</h2>

            <p>
              To access the store, please enter your email and the access code
              associated with this redemption page. Please reach out to your
              company’s POC if you haven’t received the access code.
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapState } from 'vuex'
import { progressStatus, layouts } from '@/helpers'
import VTooltip from 'v-tooltip'
import FooterLinks from '@/components/Universal/FooterLinks'
import OktaSignIn from '../AuthButtons/OktaSignIn'

Vue.use(VTooltip)

export default {
  name: 'ProjectRecipientLogin',
  props: ['uuid'],
  components: { FooterLinks, OktaSignIn },

  data() {
    return {
      innerWidth: window.innerWidth,
      selectedItems: [],
      showValidationError: false,
      showOptionsError: false,
      errorMessage: '',
      selectedTier: [],
      gemnoteBackground: require('../../assets/project_bg.png'),
      showError: false,
      email: '',
      emailValid: false
    }
  },

  computed: {
    loadStatus() {
      return progressStatus(this.loadingProjectLinkInfo)
    },

    hideSSO() {
      return this.projectInfo && this.projectInfo.teamName === 'OpenAI GTM'
    },

    disabled() {
      return Object.keys(this.projectInfo).length > 0 ? false : true
    },

    disableButton() {
      return this.validatingRecipient || !this.emailValid
    },

    isNotFound() {
      return (
        this.projectInfo.orderFinalized ||
        !this.projectInfo.enableCollectionLink
      )
    },

    teamBackground() {
      return (
        (this.projectInfo &&
          this.projectInfo.teamCustomBackground &&
          this.projectInfo.teamCustomBackground.url) ||
        this.gemnoteBackground
      )
    },
    projectTitle() {
      return (this.projectInfo && this.projectInfo.name) || ''
    },

    projectDescription() {
      return (
        (this.projectInfo && this.projectInfo.adminOrderDescription) ||
        'Please select your choice(s) below.'
      )
    },

    ...mapState({
      loggingIn: state => state.auth.loggingIn,
      loadingProjectLinkInfo: state => state.project.loadingProjectLinkInfo,
      projectInfo: state => state.project.projectInfo,
      recipientInfo: state => state.project.recipientInfo,
      verificationEmailSent: state => state.project.verificationEmailSent,
      validatingRecipient: state => state.groupOrder.validatingRecipient
    })
  },

  async created() {
    this.$store.dispatch('layout/setPageLayout', layouts.DEFAULT_LAYOUT)
    await this.$store.dispatch('project/getProjectLinkInfo', {
      projectId: this.uuid,
      redirect: true
    })
    this.handleAuthentication()
  },

  methods: {
    validateDomain() {
      let emailDomain = this.email.split('@')[1]
      if (this.projectInfo.allowedDomains) {
        if (
          this.projectInfo.allowedDomains.length === 0 ||
          this.projectInfo.allowedDomains.includes(emailDomain)
        ) {
          this.emailValid = true
          this.showError = false
        } else {
          this.emailValid = false
          this.showError = true
        }
      } else {
        this.emailValid = true
        this.showError = false
      }
    },

    handleGoogleLogin() {
      const redirectUri =
        window.location.origin +
        '/admin-order-recipients/verification?uuid=' +
        this.uuid

      this.$store.dispatch('auth/fetchGoogleLoginUrl', {
        RelayState: redirectUri
      })
    },
    handleOktaSignIn() {
      const redirectUri =
        window.location.origin + '/admin-order-recipients/' + this.uuid

      this.$store.dispatch('auth/oktaSignIn', {
        RelayState: redirectUri,
        teamName: this.projectInfo.teamName
      })
    },

    validateGroupOrderRecipient(resend) {
      this.email, this.password
      this.$store.dispatch('project/sendRecipientVerificationEmail', {
        email: this.email,
        password: '',
        uuid: this.uuid,
        resend
      })
    },
    handleAuthentication() {
      const { okta_auth_token, google_auth_token } = this.$route.query
      if (okta_auth_token) {
        this.$store.dispatch('auth/oktaLogin', {
          okta_auth_token: encodeURIComponent(okta_auth_token),
          uuid: this.uuid,
          isProjectOrder: true
        })
      }
      if (google_auth_token) {
        this.$store.dispatch('auth/verifyGoogleToken', {
          google_auth_token: encodeURIComponent(google_auth_token),
          isProjectOrder: true
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/global.scss';

.logo-band {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 3.5rem;
  border-bottom: 1px solid $main-5;
}

.logo-container {
  width: 100%;
  padding: 0 2rem;
}

.gemnote-logo {
  img {
    height: 2rem;
  }
}

.custom-title {
  font-weight: normal;
  font-size: 2rem;
  text-transform: uppercase;
  color: $main-white;
}

.disclaimer {
  //   margin-top: 4rem;

  p {
    font-weight: 600;
    font-family: $inter;
    color: #fff;

    &.sent {
      width: 90%;
    }

    &.resend {
      font-weight: 400;
      font-size: 0.875rem;

      span {
        text-decoration: underline;
        font-weight: 600;
        cursor: pointer;
      }
    }
  }
}

.row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(45%, 1fr));
  grid-template-rows: auto;
  height: 100vh;
}

.left-column {
  background-size: cover;
  /* background-position: center; */
}

.right-column {
  background: $onyx;
  color: white !important;
  flex-direction: column;
  display: flex;
  justify-content: space-between;

  .footer-links {
    padding: 0 8rem 2rem 8rem;
  }
}

.main-input-form-container {
  background: $onyx;
  padding: 8rem;
}

.custom-description {
  font-family: $inter;
  white-space: pre-line;
  color: $main-white;
}

.disabled {
  opacity: 0.5;
  pointer-events: none;
}

h2 {
  text-align: left;
  margin-bottom: 3.75rem;
}

p {
  margin-top: 2rem;
  opacity: 0.8;
  text-align: left;
  font-weight: normal;

  &:nth-child(2) {
    margin-top: 2.5rem;
  }

  &.thanks {
    margin-top: 0.75rem;
  }
}

form {
  width: 100%;
  max-width: 20rem;
  margin: 2.5rem 0;
  text-align: left;

  .form-group input {
    color: white !important;
    border-bottom: 1px solid $white !important;
    opacity: 0.8;
    padding-left: 2px !important;
    transition: all 0.5s linear;

    &[type='email'] {
      height: 2.5rem;
    }

    &[type='password'] {
      height: 2.5rem;
    }
  }

  .form-group .input-error {
    border-bottom: 1px solid #eb9884 !important;
  }

  .error-text {
    color: #eb9884;
    font-size: 12px;
  }

  .whitebutton-gemnote {
    background-color: white !important;
    height: 2.75rem;
    width: 6.438rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
  }

  .login-button {
    padding: 1.75rem 0;
    text-align: left;
  }
}

.login-button {
  padding: 1.75rem 0;
  text-align: left;
}

.option-region {
  text-align: left;
  // margin-top: 1.75rem;

  p {
    width: 24%;
    text-align: center;
    border-bottom: 1px solid #a09c95;
    color: #a09c95;
    font-size: 1rem;
    font-weight: normal;
    line-height: 0.1em;
    margin: 10px 0 20px;

    span {
      background: #232220;
      padding: 0 10px;
    }
  }

  .sso-sign-in {
    margin-top: 2.75rem;
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: center;
    gap: 1rem;
    width: 100%;
  }

  .google-btn {
    height: 45px;
    padding: 0 12px;
    background: transparent;
    display: flex;
    align-items: center;
    font-family: 'Inter', sans-serif;
    border: 1px solid #f5f2ee;

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    span {
      margin-left: 0.5rem;
      font-weight: inherit;
      text-transform: none !important;
    }
  }

  .okta-btn {
    height: 45px !important;
    border: 1px solid #f5f2ee;
    color: #f5f2ee !important;
    background-color: transparent !important;
    width: 100%;
  }
}

@media screen and (max-width: 1500px) and (min-width: 900px) {
  .main-input-form-container {
    padding: 6rem;
  }

  .footer-links {
    padding: 6rem !important;
  }
}

@media (max-width: 1100px) {
  .row {
    grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
    transition: 1s ease-in;
  }

  .left-column {
    height: 50vh;
  }

  .right-column {
    height: 100vh;
  }

  .main-input-form-container {
    padding: 2rem;
  }

  .footer-links {
    padding: 2rem !important;
  }

  form {
    .login-button {
      padding-top: 1rem;
      padding-bottom: 1rem;
    }

    p {
      margin: 0rem 0rem;
    }
  }
  .sso-sign-in {
    width: 100%;
  }
}

@media (min-width: 576px) and (max-width: 767.98px) {
  .form-holder {
    width: 100% !important;
  }
  .sso-sign-in {
    width: 70%;
  }
}
@media (min-width: 576px) {
  .option-region {
    .sso-sign-in {
      width: 50%;
    }
  }
  form {
    .login-button {
      width: 50%;
    }
  }
}
@media (min-width: 1200px) {
  .option-region {
    .sso-sign-in {
      width: 80%;
    }
  }
  form {
    .login-button {
      width: 25%;
    }
  }
}
</style>
